import { Injectable } from '@angular/core';
import { AnalyticsUser } from '@model/user/analytics-user';
import { User } from '@model/user/user';
import { UserRegistrationService } from '@service/netmera/user-registration.service';
import { CommonModalComponent } from '@shared/components/modal/common-modal/common-modal.component';
import { LocalSettingsKey, getUserLocal } from '@shared/types/local-settings';
import { ActiveSession } from '@shared/types/sso';
import { BsModalService } from 'ngx-bootstrap/modal';
import { first, Subject } from 'rxjs';
import { Router } from '@angular/router';
import { LoginService } from './login.service';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@service/notification.service';
import { ImageDownloadLogsService } from '@service/utils/image-download-logs.service';

@Injectable({
  providedIn: 'root',
})
export class SessionUtilsService {
  constructor(
    private router: Router,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private modalService: BsModalService,
    private userRegistrationService: UserRegistrationService,
    private loginService: LoginService
  ) {}

  public static clearLocalStorage(service: ImageDownloadLogsService): void {
    const logoutedSession = localStorage.getItem(LocalSettingsKey.LOGOUTED_SESSION);
    localStorage.removeItem(LocalSettingsKey.USER);
    localStorage.removeItem(LocalSettingsKey.DEVICE_ID);
    localStorage.removeItem(LocalSettingsKey.IS_TOKEN_RENEWING);
    this.clearChangedSessionStorage();
    if (logoutedSession) {
      // Birden fazla oturum açıkken birinden çıkış yapıldığında çıkış yapılan oturumun mesajlarını temizle
      service.clearMessagesForUser(logoutedSession);
    } else {
      // Birden fazla oturum açık değilken bir hesaptan çıkış yapıldığında tüm mesajları temizle
      service.clearAll();
    }
  }

  public static clearChangedSessionStorage(): void {
    localStorage.removeItem(LocalSettingsKey.CALENDAR_EVENTS);
    localStorage.removeItem(LocalSettingsKey.CALENDAR_START_TIME);
    localStorage.removeItem(LocalSettingsKey.CALENDAR_UPDATE_TIME);
    localStorage.removeItem(LocalSettingsKey.CALENDAR_VIEW);
    localStorage.removeItem(LocalSettingsKey.PREFERENCES);
    localStorage.removeItem(LocalSettingsKey.POPOUT_EVENT);
    localStorage.removeItem(LocalSettingsKey.EMAIL_PREFERENCE);
    localStorage.removeItem(LocalSettingsKey.AI_WELCOME_SEEN);
  }

  public static tokenHasTimeByType(type?: 'refresh'): boolean {
    const user = getUserLocal();
    const tokenTime = type === 'refresh' ? user?.refresh_token_expires : user?.expire_time;
    if (!tokenTime) {
      return false;
    }
    const userAccessTokenExpires = new Date(0);
    const now = new Date(0);
    userAccessTokenExpires.setUTCSeconds(tokenTime);
    now.setUTCMilliseconds(new Date().getTime());
    const timeDiffMin = (userAccessTokenExpires.getTime() - now.getTime()) / 60000; // dakika cinsinden
    return 5 < timeDiffMin; // access token süresi 5 dakikadan küçük mü?
  }

  public setNewTokensToLocale(userData: any): void {
    const oldUser = getUserLocal() || {};
    const user = {
      ...oldUser,
      username: userData.username || oldUser.username,
      token: userData.access_token,
      expire_time: userData.expires || userData.expire_time,
      refresh_token: userData.refresh_token || userData.refresh_token_info.refresh_token,
      refresh_token_expires: userData.refresh_token_expire || userData.refresh_token_info.expire_time,
      push_notification_id: userData.push_notification_id || oldUser.push_notification_id,
    };
    localStorage.setItem(LocalSettingsKey.USER, JSON.stringify(user));
    this.saveAnalyticsUser(user);
  }

  private saveAnalyticsUser(data: any): void {
    const user = new AnalyticsUser(data.name, data.push_notification_id || data.username, localStorage.getItem(LocalSettingsKey.DEVICE_ID));
    this.userRegistrationService.saveUser(user, 'refresh-token');
  }

  askForChangeSessionApproval(email: string, refresherSubscription: Subject<boolean>): void {
    const initialState = {
      modalTitleText: 'MAILBOX_CHANGE_SESSION_APPROVAL_MODAL_TITLE',
      modalBodyTextKey: 'MAILBOX_CHANGE_SESSION_APPROVAL_MODAL_DESC',
      modalDeclineText: 'BUTTON_CANCEL',
      modalAcceptText: 'BUTTON_CONTINUE',
    };
    const approvalModal = this.modalService.show(CommonModalComponent, {
      class: 'modal-dialog-centered modal-small',
      initialState,
      ignoreBackdropClick: true,
    });
    approvalModal.content.onClose.subscribe(response => {
      if (response === true) {
        this.changeSession(email, refresherSubscription);
      } else {
        this.router.navigate(['/m']);
      }
    });
  }

  changeSession(email: string, refresherSubscription: Subject<boolean>): void {
    localStorage.setItem(LocalSettingsKey.ACTIVE_SESSION_CHANGES, 'true');
    const activeSessions = JSON.parse(localStorage.getItem(LocalSettingsKey.ACTIVE_SESSIONS));
    const newUserInfo = activeSessions[email] as ActiveSession;
    if (newUserInfo) {
      newUserInfo.email = email;
      this.loginService
        .validateToken(newUserInfo.token)
        .pipe(first())
        .subscribe(
          () => {
            // token geçerli
            this.saveNewUserAndReload(newUserInfo, false);
            refresherSubscription.next(true);
          },
          err => {
            if (err.status === 404) {
              // token geçersiz
              this.generateNewToken(newUserInfo, refresherSubscription);
            }
          }
        );
    }
  }

  generateNewToken(session: ActiveSession, refresherSubscription: Subject<boolean>): void {
    const formData = new FormData();
    formData.append('email', session.email);
    this.loginService
      .generateToken(formData)
      .pipe(first())
      .subscribe(
        res => {
          const activeSessions = JSON.parse(localStorage.getItem(LocalSettingsKey.ACTIVE_SESSIONS));
          session.token = res.access_token;
          session.expire_time = res.expire_time;
          session.refresh_token = res.refresh_token_info.refresh_token;
          session.refresh_token_expires = res.refresh_token_info.expire_time;

          activeSessions[session.email] = session;

          localStorage.setItem(LocalSettingsKey.ACTIVE_SESSIONS, JSON.stringify(activeSessions));
          this.saveNewUserAndReload(session, false);
          refresherSubscription.next(true);
        },
        err => {
          this.notificationService.showError(this.translateService.instant(err.error.message));
          refresherSubscription.next(true);
        }
      );
  }

  public saveNewUserAndReload(session: ActiveSession, isReloadRequired: boolean = true): void {
    const user = new User();
    user.username = session.email;
    user.name = session.full_name;
    user.token = session.token;
    user.expire_time = session.expire_time;
    user.refresh_token = session.refresh_token;
    user.refresh_token_expires = session.refresh_token_expires;
    user.push_notification_id = session.push_notification_id;
    localStorage.setItem(LocalSettingsKey.USER, JSON.stringify(user));
    if (isReloadRequired) {
      this.reloadPageToNewSession(session.email);
    }
  }

  public reloadPageToNewSession(username: string): void {
    const pathname = window.location.pathname;
    const paths = pathname.split('/');
    SessionUtilsService.clearChangedSessionStorage();
    switch (paths[1]) {
      case 'settings':
        window.location.reload();
        break;
      case 'a':
        window.location.href = `${window.location.origin}/a/contacts`;
        break;
      case 'c':
        window.location.href = `${window.location.origin}${window.location.pathname}`;
        break;
      case 'm':
        window.location.href = `${window.location.origin}/m/${username}`;
        break;
      default:
        window.location.reload();
    }
  }
}
